import React, { useState } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// Note: Renamed component to reflect common file naming convention
export default function LoginScreen({ setIsLoggedIn }) { 
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false); // NEW: Loading state for feedback
  const [tenant, setTenant] = useState(localStorage.getItem("tenant") || "");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors
    setLoading(true); // Start loading

    const apiUrl = process.env.REACT_APP_API_URL;

    if (!apiUrl) {
      setError("Configuration Error: REACT_APP_API_URL is not set.");
      console.error("REACT_APP_API_URL is missing.");
      setLoading(false); // Stop loading on error
      return;
    }

    try {
      const response = await axios.post(
        `${apiUrl}/web-login`, 
        { email, password },
        {
          withCredentials: true,
          headers: {
            "X-Tenant": localStorage.getItem("tenant") || ""
          }
        }
      );

      const { user } = response.data;

      // Also keep session version if needed by UI
      sessionStorage.setItem("user", JSON.stringify(user));
      sessionStorage.setItem("session_valid", "yes");
      setIsLoggedIn(true);
      navigate("/");

    } catch (error) {
      // Improved error handling
      console.error("Login API Error:", error.response || error);
      setError(error.response?.data?.message || "Invalid credentials or network error.");
    } finally {
      setLoading(false); // Stop loading regardless of success or failure
    }
  };
  
  return (
    <Grid container columns={12} sx={{ height: "100vh" }}>
    {/* LEFT SIDE (66.6% or 8/12) - Using standard integers for cleaner look */}
    <Grid
      size={{ xs: 12, md: 7 }}
      sx={{
        backgroundColor: "#2e7d32",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        paddingLeft: { xs: 3, md: 10 },
        color: "white",
      }}
    >
      <Typography variant="h3" fontWeight="bold">
        OnTime Admin Portal
      </Typography>
      <Typography variant="h6" sx={{ mt: 2, opacity: 0.9 }}>
        Secure access for HR, Manager & Admin Only.
      </Typography>
    </Grid>

    {/* RIGHT SIDE (33.3% or 4/12) - Using standard integers for cleaner look */}
    <Grid
      size={{ xs: 12, md: 5 }}
      sx={{
        backgroundColor: "#e8f5e9",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Paper
        elevation={6}
        sx={{
          padding: 5,
          width: "80%",
          maxWidth: 420,
          borderRadius: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold" align="center" gutterBottom>
          Login
        </Typography>

        {error && (
          <Typography color="error" align="center" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Bind handleSubmit to the form */}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            required
            label="Tenant Code"
            margin="normal"
            value={tenant}
            onChange={(e) => {
              setTenant(e.target.value);
              localStorage.setItem("tenant", e.target.value);
            }}
            disabled={loading}
          />
          <TextField
            fullWidth
            required
            label="Email Address"
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading} // Disable input while loading
            slotProps={{
              input: { autoComplete: "username" }
            }}
          />

          <TextField
            fullWidth
            required
            type="password"
            label="Password"
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading} // Disable input while loading
            slotProps={{
              input: { autoComplete: "current-password" }
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading} // Disable button while loading/submitting
            sx={{
              mt: 3,
              py: 1.5,
              backgroundColor: "#2e7d32",
              "&:hover": { backgroundColor: "#1b5e20" },
              borderRadius: 2,
            }}
          >
            {loading ? "LOGGING IN..." : "LOGIN"}
          </Button>
        </Box>
      </Paper>
    </Grid>
  </Grid>
  );
};